import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, extractError } from "@/lib/api";
import { formatTimeID } from "@/lib/format";
import { ChevronLeft, Send, MessageCircle, Megaphone } from "lucide-react";
import { toast } from "sonner";

export default function StoreChat() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef();

  const load = () => api.get("/chat/thread").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    if (!user || user.role !== "pelanggan") return;
    const draft = localStorage.getItem("kiyana_chat_draft");
    if (draft) {
      setText(draft);
      localStorage.removeItem("kiyana_chat_draft");
    }
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [user]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data?.messages?.length]);

  if (!user) {
    return (
      <div className="text-center py-16">
        <MessageCircle className="w-12 h-12 text-cocoa-muted mx-auto mb-3" />
        <div className="text-sm text-cocoa-muted mb-4">Masuk untuk chat dengan admin Kiyana</div>
        <Link to="/store/auth" data-testid="chat-login-btn" className="px-6 h-11 leading-[44px] inline-block rounded-full bg-brand text-white text-sm font-semibold">Masuk</Link>
      </div>
    );
  }

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post("/chat/messages", { text: text.trim() });
      setText("");
      load();
    } catch (e) { toast.error(extractError(e)); } finally { setBusy(false); }
  };

  const msgs = data?.messages || [];

  return (
    <div className="flex flex-col h-[calc(100vh-180px)] animate-fade-up">
      <div className="flex items-center justify-between pb-3">
        <button data-testid="chat-back" onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-white border border-blush-soft flex items-center justify-center">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="text-center">
          <h1 className="font-semibold">Chat Admin Kiyana</h1>
          <div className="text-[10px] text-cocoa-muted">Biasanya membalas dalam beberapa menit • Senin–Sabtu 08.30–16.00 WIB</div>
        </div>
        <div className="w-9" />
      </div>

      <div data-testid="chat-messages" className="flex-1 overflow-y-auto space-y-2.5 bg-white rounded-2xl border border-blush-soft p-3.5">
        {msgs.length === 0 && (
          <div className="text-center py-10 text-xs text-cocoa-muted">
            Belum ada pesan. Tanya apa saja soal produk, pesanan, atau pengiriman — admin siap membantu!
          </div>
        )}
        {msgs.map((m) => (
          <div key={m.id} data-testid={`chat-msg-${m.id}`} className={`flex ${m.sender === "customer" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 ${m.sender === "customer" ? "bg-brand text-white rounded-br-md" : "bg-blush text-cocoa rounded-bl-md"}`}>
              {m.sender !== "customer" && (
                <div className="text-[9px] font-bold opacity-70 mb-0.5 flex items-center gap-1">
                  {m.broadcast && <Megaphone className="w-2.5 h-2.5" />}
                  {m.sender_name || "Admin Kiyana"}{m.auto ? " • Balasan Otomatis" : ""}{m.broadcast ? " • Broadcast" : ""}
                </div>
              )}
              <div className="text-xs leading-relaxed whitespace-pre-line">{m.text}</div>
              <div className={`text-[9px] mt-1 ${m.sender === "customer" ? "text-white/70 text-right" : "text-cocoa-muted"}`}>{formatTimeID(m.created_at)}</div>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="flex gap-2 pt-3">
        <input
          data-testid="chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Tulis pesan..."
          className="flex-1 h-11 px-4 rounded-full border border-blush-soft bg-white text-sm focus:outline-none focus:ring-2 ring-brand"
        />
        <button data-testid="chat-send-btn" onClick={send} disabled={busy || !text.trim()} className="w-11 h-11 rounded-full bg-brand text-white flex items-center justify-center disabled:opacity-40">
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
